import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  Modal,
  TouchableOpacity,
  Dimensions,
  Platform
} from 'react-native';
import { CommonActions } from '@react-navigation/native';

import AsyncStorage from '@react-native-async-storage/async-storage';
import { loginWithToken } from '../services/authApi';

const { width, height } = Dimensions.get('window');

export default function TokenLoginScreen({ navigation, route }) {
  const [isLoading, setIsLoading] = useState(true);
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const [token, setToken] = useState('');
  const [backUrl, setBackUrl] = useState('');

  // Custom Alert Modal State
  const [alertModal, setAlertModal] = useState({
    visible: false,
    title: '',
    message: '',
    type: 'error', // 'error' or 'success'
  });

  const showCustomAlert = (title, message, type = 'error', onConfirm = null) => {
    setAlertModal({
      visible: true,
      title,
      message,
      type,
      onConfirm
    });
  };

  const hideCustomAlert = () => {
    setAlertModal(prev => ({ ...prev, visible: false }));
  };

  const handleAlertConfirm = () => {
    // Call the onConfirm callback if provided
    if (alertModal.onConfirm) {
      alertModal.onConfirm();
    }
    hideCustomAlert();
    if (status === 'success') {
      // On successful login, we don't need to navigate manually
      // The app's NavigationWrapper will automatically detect the authentication state change
      // and switch from AuthStackNavigator to MainStackNavigator
      console.log('Success alert confirmed - app will automatically navigate to main screen');
    } else {
      // Navigate back to login screen on error
      navigation.navigate('Login');
    }
  };

  const processTokenLogin = async () => {
    try {
      setIsLoading(true);
      setStatus('processing');
      
      // Get token from route params or AsyncStorage
      let loginToken = route.params?.token;
      let loginBackUrl = route.params?.backUrl;

      // Device reward params — prefer route.params, fallback to AsyncStorage
      let actionParam      = route.params?.action;
      let rewardTokenParam = route.params?.reward_token;
      let hexpodUrlParam   = route.params?.hexpod_url;
      
      if (!loginToken) {
        // Try to get token from AsyncStorage
        loginToken = await AsyncStorage.getItem('pending_login_token');
        loginBackUrl = await AsyncStorage.getItem('pending_back_url');

        // Also read reward params that were stored by the URL handler
        if (!actionParam)      actionParam      = await AsyncStorage.getItem('pending_action');
        if (!rewardTokenParam) rewardTokenParam = await AsyncStorage.getItem('pending_reward_token');
        if (!hexpodUrlParam)   hexpodUrlParam   = await AsyncStorage.getItem('pending_hexpod_url');
        
        // Clear all stored params after reading
        await AsyncStorage.multiRemove([
          'pending_login_token',
          'pending_back_url',
          'pending_action',
          'pending_reward_token',
          'pending_hexpod_url',
        ]);
      }
      
      if (!loginToken) {
        throw new Error('No login token found');
      }
      
      setToken(loginToken);
      setBackUrl(loginBackUrl || '');
      
      console.log('Processing token login with token:', loginToken ? 'Present' : 'Missing');
      
      // Call the token login API
      const result = await loginWithToken(loginToken, loginBackUrl);
      
      if (result.success) {
        console.log('Token login successful');
        setStatus('success');

        // ── Device Reward flow ──
        // Store params for NavigationWrapper's handlePendingDeviceReward()
        // to navigate after auth switches to MainStack (avoids cross-stack errors)

        if (actionParam === 'device_reward' && rewardTokenParam) {
          console.log('Device reward action detected — storing params, triggering auth transition');
          await AsyncStorage.setItem('pending_action', 'device_reward');
          await AsyncStorage.setItem('pending_reward_token', rewardTokenParam);
          if (hexpodUrlParam) await AsyncStorage.setItem('pending_hexpod_url', hexpodUrlParam);

          if (Platform.OS === 'web') {
            // Reload: NavigationWrapper will see isAuthenticated=true + pending_action → navigate to DeviceReward
            window.location.reload();
          } else {
            // Native: trigger auth re-check via forceAuthCheck global or navigation reset to MainTabs
            if (typeof window !== 'undefined' && window.forceAuthCheck) {
              window.forceAuthCheck();
            } else {
              navigation.dispatch(CommonActions.reset({ index: 0, routes: [{ name: 'MainTabs' }] }));
            }
          }
          return;
        }
        
        // Show success message and let the app's authentication state
        // automatically handle the navigation to the main app
        showCustomAlert(
          'Login Berhasil',
          'Anda berhasil login menggunakan token. Selamat datang!',
          'success',
          () => {
            // This callback will be called when the user dismisses the alert
            // Force navigation to main app to ensure redirection works
            console.log('Success alert dismissed, forcing navigation to main app');
            
            // Handle navigation differently for web vs native
            if (Platform.OS === 'web') {
              // For web, use window.location to force a full page reload
              // This ensures the NavigationWrapper re-evaluates authentication state
              window.location.reload();
            } else {
              // For native, use navigation reset to completely replace the navigation stack
              // This ensures we start fresh with the main app navigation
              navigation.dispatch(
                CommonActions.reset({
                  index: 0,
                  routes: [{ name: 'MainTabs' }],
                })
              );
            }
          }
        );
      } else {
        throw new Error(result.message || 'Token login failed');
      }
      
    } catch (error) {
      console.error('Token login error:', error);
      setStatus('error');
      setErrorMessage(error.message);
      
      showCustomAlert(
        'Login Gagal, Error:',
        error.message || 'Terjadi kesalahan saat proses login. Silakan coba lagi.',
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    processTokenLogin();
  }, []);

  return (
    <View style={styles.container}>
      {isLoading && (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#007AFF" />
          <Text style={styles.loadingText}>
            {status === 'processing' ? 'Memproses login...' : 'Login berhasil, mengalihkan...'}
          </Text>
        </View>
      )}

      {!isLoading && status === 'error' && (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>Login gagal, Error: {errorMessage}</Text>
          <TouchableOpacity 
            style={styles.retryButton}
            onPress={processTokenLogin}
          >
            <Text style={styles.retryButtonText}>Coba Lagi</Text>
          </TouchableOpacity>
          <TouchableOpacity 
            style={styles.backButton}
            onPress={() => navigation.navigate('Login')}
          >
            <Text style={styles.backButtonText}>Kembali ke Login</Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Custom Alert Modal */}
      <Modal
        visible={alertModal.visible}
        transparent={true}
        animationType="fade"
        onRequestClose={hideCustomAlert}
      >
        <View style={styles.modalOverlay}>
          <View style={[
            styles.modalContainer,
            alertModal.type === 'error' ? styles.modalError : styles.modalSuccess
          ]}>
            <Text style={styles.modalTitle}>{alertModal.title}</Text>
            <Text style={styles.modalMessage}>{alertModal.message}</Text>
            <TouchableOpacity
              style={styles.modalButton}
              onPress={handleAlertConfirm}
            >
              <Text style={styles.modalButtonText}>OK</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingContainer: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#666',
  },
  errorContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  errorText: {
    fontSize: 16,
    color: '#FF3B30',
    textAlign: 'center',
    marginBottom: 20,
  },
  retryButton: {
    backgroundColor: '#007AFF',
    padding: 12,
    borderRadius: 8,
    marginBottom: 12,
    minWidth: 120,
  },
  retryButtonText: {
    color: '#fff',
    textAlign: 'center',
    fontWeight: '600',
  },
  backButton: {
    backgroundColor: '#8E8E93',
    padding: 12,
    borderRadius: 8,
    minWidth: 120,
  },
  backButtonText: {
    color: '#fff',
    textAlign: 'center',
    fontWeight: '600',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContainer: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 24,
    width: Math.min(width, 430) - 32,
    alignItems: 'center',
  },
  modalError: {
    borderTopWidth: 4,
    borderTopColor: '#FF3B30',
  },
  modalSuccess: {
    borderTopWidth: 4,
    borderTopColor: '#34C759',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 12,
    textAlign: 'center',
  },
  modalMessage: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 20,
    color: '#666',
  },
  modalButton: {
    backgroundColor: '#007AFF',
    padding: 12,
    borderRadius: 8,
    minWidth: 80,
  },
  modalButtonText: {
    color: '#fff',
    textAlign: 'center',
    fontWeight: '600',
  },
});